import {
  Flex,
  ButtonGroup,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Icon,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useCallback, useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { MultipleChoiceFilter } from '@duke-hq/ui/components/MultipleChoice/MultipleChoiceFilter';
import { RangeDatepicker } from 'chakra-dayzed-datepicker';
import type { MultipleChoiceFilterOption } from '@duke-hq/ui/components/MultipleChoice/MultipleChoiceFilterTypes';
import {
  BillableItemStatusSchema,
  type BillableItemStatus,
} from '@duke-hq/svc-api-db/schemas/enums/BillableItemStatus.schema';
import { datesEqual, EMPTY_STATUS, isBillableStatus } from './utils';
import { djs } from '@duke-hq/djs';

interface BillableItemsFiltersProps {
  filters: {
    fromDate?: string;
    toDate?: string;
    providerIds?: string;
    status?: string;
  };
  providerOptions: { id: string; value: string; label: string }[];
  onChange: (filters: Record<string, string | undefined>) => void;
  loading?: boolean;
}

export function BillableItemsFilters({
  filters,
  providerOptions,
  onChange,
  loading,
}: BillableItemsFiltersProps) {
  const providerIds = filters.providerIds ? filters.providerIds.split(',') : [];
  const status = useMemo(
    () => (filters.status ? filters.status.split(',').filter(isBillableStatus) : []),
    [filters.status],
  );

  const defaultEnd = useMemo(() => djs().endOf('day').toDate(), []);
  const defaultStart = useMemo(() => djs().subtract(5, 'days').startOf('day').toDate(), []);
  const dateRange = {
    start: filters.fromDate ? new Date(filters.fromDate) : defaultStart,
    end: filters.toDate ? new Date(filters.toDate) : defaultEnd,
  };

  type Option<T extends string = string> = { id: T; value: T; label: string };
  const statusOptions = useMemo<Option<BillableItemStatus>[]>(
    () => BillableItemStatusSchema.options.map((s) => ({ id: s, value: s, label: s })),
    [],
  );

  const effectiveStatusArray: string[] = useMemo(() => [...(status ?? EMPTY_STATUS)], [status]);

  const handleFilterChange = useCallback(
    (filterOptions: MultipleChoiceFilterOption) => {
      const statusRaw = filterOptions['statusId'];
      const providerRaw = filterOptions['providerId'];

      const partial: Record<string, string | undefined> = {};

      if (statusRaw !== undefined) {
        const validStatuses = statusRaw.filter(isBillableStatus);
        partial.status = validStatuses.length > 0 ? validStatuses.join(',') : undefined;
      }

      if (providerRaw !== undefined) {
        partial.providerIds = providerRaw.length > 0 ? providerRaw.join(',') : undefined;
      }

      onChange(partial);
    },
    [onChange],
  );

  const [dateDraft, setDateDraft] = useState<Date[]>([]);
  const effectiveSelectedDates = useMemo<Date[]>(() => {
    if (dateDraft.length === 1) {
      return dateDraft;
    }
    return [dateRange.start, dateRange.end];
  }, [dateDraft, dateRange.start, dateRange.end]);

  const handleDatepickerChange = useCallback(
    (dates: Date[]) => {
      setDateDraft(dates);
      if (dates.length === 2 && dates[0] && dates[1]) {
        const [start, end] = dates;
        if (!datesEqual(start, dateRange.start) || !datesEqual(end, dateRange.end)) {
          onChange({
            fromDate: start.toISOString(),
            toDate: end.toISOString(),
          });
        }
        setDateDraft([]);
      }
    },
    [dateRange.end, dateRange.start, onChange],
  );

  const handleDateRangeClick = useCallback(
    (days: number) => {
      const start = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
      const end = new Date();
      onChange({
        fromDate: start.toISOString(),
        toDate: end.toISOString(),
      });
    },
    [onChange],
  );

  return (
    <Flex gap={2} wrap="wrap">
      <MultipleChoiceFilter
        items={providerOptions}
        label="Provider"
        name="providerId"
        value={providerIds.length ? providerIds.map(String).join(',') : ''}
        onFilterChange={handleFilterChange}
      />
      <MultipleChoiceFilter
        items={statusOptions}
        label="Status"
        name="statusId"
        value={effectiveStatusArray.length ? effectiveStatusArray.join(',') : ''}
        onFilterChange={handleFilterChange}
      />
      <ButtonGroup
        key="date-range"
        isAttached
        size="sm"
        variant="outline"
        isDisabled={loading === true}
      >
        <RangeDatepicker
          selectedDates={effectiveSelectedDates}
          onDateChange={handleDatepickerChange}
          configs={{ dateFormat: 'dd-MM-yyyy' }}
        />
        <Menu>
          <MenuButton
            as={IconButton}
            aria-label={t`Date range select options`}
            icon={<Icon as={ChevronDown} boxSize={4} />}
            borderLeftRadius={0}
          />
          <MenuList>
            <MenuItem onClick={() => handleDateRangeClick(1)}>
              <Trans>Last 24 hours</Trans>
            </MenuItem>
            <MenuItem onClick={() => handleDateRangeClick(5)}>
              <Trans>Last 5 days</Trans>
            </MenuItem>
            <MenuItem onClick={() => handleDateRangeClick(7)}>
              <Trans>Last 7 days</Trans>
            </MenuItem>
            <MenuItem onClick={() => handleDateRangeClick(30)}>
              <Trans>Last 30 days</Trans>
            </MenuItem>
          </MenuList>
        </Menu>
      </ButtonGroup>
    </Flex>
  );
}
